<script setup lang="ts">
  import { ref, computed, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { helpers } from '@vuelidate/validators'
  import FlowCard from '@/shared/components/FlowCard/FlowCard.vue'
  import { useUserStore, useCommonStore } from '@/shared/store'
  import { useValidation, useValidators } from '@/shared/composables'
  import {
    PROCEED_EVENT_NAME,
    LOAD_EVENT_NAME,
    UAE_PHONE_CODE,
    INBOUND_MAX_AGE,
    OUTBOUND_MAX_AGE,
    INBOUND_TRANSLATE_HASH,
    OUTBOUND_TRANSLATE_HASH,
    GENDER_MALE,
  } from './FlowPersonalInformationWidget.constants'
  import { BaseButton, BaseInput, ToggleInput, PhoneInput } from '@qic/ui-components'
  import BirthDateInput from '@/entities/Flow/components/BirthDateInput/BirthDateInput.vue'
  import { DEFAULT_START_DATE } from '@/entities/Flow/components/BirthDateInput/BirthDateInput.constants'
  import NationalityInput from '@/entities/Flow/components/NationalityInput/NationalityInput.vue'
  import EmiratesIdInput from '@/entities/Flow/components/EmiratesIdInput/EmiratesIdInput.vue'
  import PasportNumberInput from '@/entities/Flow/components/PasportNumberInput/PasportNumberInput.vue'
  import ContactsIcon from '@/shared/components/icons/ContactsIcon.vue'
  import BaseEmailInput from '@/shared/components/BaseEmailInput/BaseEmailInput.vue'
  import BaseFoldingBlock from '@/shared/components/BaseFoldingBlock/BaseFoldingBlock.vue'
  import FlowDataProtectReminder from '@/entities/Flow/components/FlowDataProtectReminder'
  import { TravelDirection } from '@/shared/types/common.types'
  import { getUnixTimeStamp } from '@/shared/utils/UnixTime'
  import { useGoogleAnalytics } from '@/shared/composables/useGoogleAnalytics/useGoogleAnalytics'
  import type { Nationality } from '@/shared/types/api.types'
  import { pushEvent } from '@/shared/composables/usePushEvent'
  import { formatAnalyticsDate } from '@/shared/services/GoogleAnalyticsService'
  import QIDExpiryDateInput from '@/entities/Flow/components/QIDExpiryDateInput/QIDExpiryDateInput.vue'
  import BaseRadio from '@/shared/components/BaseRadio/BaseRadio.vue'

  interface Props {
    isEdit?: boolean
    withWinterSport?: boolean
    winterSportPrice?: number
    typeOfTravel?: Exclude<TravelDirection, TravelDirection.UNKNOWN>
    loadingFormData?: boolean
  }

  interface Emits {
    (e: typeof PROCEED_EVENT_NAME): void
    (e: typeof LOAD_EVENT_NAME, request: () => Promise<any>, requestId?: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    isEdit: false,
    withWinterSport: true,
    winterSportPrice: 0,
    typeOfTravel: TravelDirection.OUTBOUND,
    loadingFormData: false,
  })
  const emit = defineEmits<Emits>()

  const userStore = useUserStore()
  const commonStore = useCommonStore()
  const { t } = useI18n()
  const gaService = useGoogleAnalytics()

  const firstName = ref(userStore.firstName ?? '')
  const familyName = ref(userStore.familyName ?? '')
  const dateOfBirth = ref(userStore.dateOfBirth)
  const nationality = ref(userStore.nationality)
  const nationalityCode = ref(nationality.value?.nationalityCode || '')
  const emiratesId = ref(userStore.emiratesId ?? '')
  const civilIdExpiryDate = ref(userStore.civilIdExpiryDate)
  const passportNumber = ref(userStore.passportNumber ?? '')
  const email = ref(userStore.email ?? '')
  const emailErrorMessage = ref('')
  const phone = ref(userStore.phone ?? '')
  const maskedPhone = ref(userStore.maskedPhone ?? '')
  const isWinterSportChecked = ref(userStore.withWinterSport)
  const countryCode = ref(userStore.countryCode ?? UAE_PHONE_CODE)
  const gender = ref(userStore.gender ?? GENDER_MALE)

  const isOutbound = props.typeOfTravel === TravelDirection.OUTBOUND
  const translatesHash = isOutbound ? OUTBOUND_TRANSLATE_HASH : INBOUND_TRANSLATE_HASH

  const phoneCompleted = ref(false)

  const nationalities = computed(() => commonStore.nationalities)
  const maxAge = computed(() => (isOutbound ? OUTBOUND_MAX_AGE : INBOUND_MAX_AGE))

  const { emiratesIdValidator, emailValidator, phoneValidator, required } = useValidators()
  const emiratesIdRules = computed(() => (isOutbound ? { ...emiratesIdValidator } : {}))
  const civilIdExpiryDateRules = computed(() => (isOutbound ? { required } : {}))

  const validationRules = computed(() => {
    const commonRules = {
      firstName: { required },
      familyName: { required },
      dateOfBirth: { required },
      nationalityCode: { required },
      emiratesId: emiratesIdRules.value,
      civilIdExpiryDate: civilIdExpiryDateRules.value,
      passportNumber: { required },
      gender: { required },
    }

    const additionalRules: any = {}
    if (props.isEdit) {
      additionalRules.email = {
        ...emailValidator,
        errorMessage: helpers.withMessage(
          t('qic.error.email.format'),
          () => !emailErrorMessage.value
        ),
      }

      additionalRules.phone = { required }
      additionalRules.phoneCompleted = { ...phoneValidator }
    }

    return { ...commonRules, ...additionalRules }
  })

  const { v$, errors } = useValidation(validationRules, {
    firstName,
    familyName,
    dateOfBirth,
    nationalityCode,
    emiratesId,
    civilIdExpiryDate,
    passportNumber,
    email,
    phone,
    phoneCompleted,
    gender,
  })
  const errorsCustom = ref([])
  let maxBirthdate = new Date()
  let minBirthdate = new Date()

  const initBirthdate = (max: Date, min: Date) => {
    maxBirthdate = max
    minBirthdate = min
  }

  const validateEmail = () =>
    commonStore.validateEmail(email.value).then((message) => {
      emailErrorMessage.value = message
      return message
    })

  const nationalityName = computed(
    () =>
      nationalities.value.find((option) => option.nationalityCode === nationalityCode.value)
        ?.nationalityName
  )

  const onProceed = async () => {
    v$.value.$touch()

    if (!v$.value.$error && !errorsCustom.value.length) {
      await emit(LOAD_EVENT_NAME, async () => {
        let message = ''
        if (props.isEdit) {
          message = await validateEmail()
        }

        if (message) {
          return
        }

        userStore.setFirstName(firstName.value, true)
        userStore.setFamilyName(familyName.value, true)
        userStore.setDateOfBirth(dateOfBirth.value, true)
        userStore.setNationality(nationality.value, true)
        userStore.setPassportNumber(passportNumber.value, true)
        userStore.setMaskedPhone(maskedPhone.value, true)
        userStore.setGender(gender.value, true)

        if (props.isEdit) {
          userStore.setEmail(email.value, true)
          userStore.setPhone(phone.value, true)
        }

        if (isOutbound) {
          userStore.setEmiratesId(emiratesId.value, true)
          userStore.setCivilIdExpiryDate(civilIdExpiryDate.value, true)
          if (props.withWinterSport) userStore.setWithWinterSport(isWinterSportChecked.value, true)
        } else {
          userStore.setCountryCode(countryCode.value, true)
        }

        const winterSportData =
          isOutbound && props.withWinterSport
            ? {
                winSports: isWinterSportChecked.value ? 1 : 0,
              }
            : {}

        const data: any = {
          firstName: firstName.value.trim(),
          familyName: familyName.value.trim(),
          dateOfBirth: getUnixTimeStamp(dateOfBirth.value as Date),
          nationalityCode: nationalityCode.value,
          nationalityName: nationalityName.value,
          qatarId: isOutbound ? emiratesId.value : '',
          passportNumber: passportNumber.value,
          gender: gender.value,
          ...winterSportData,
        }

        if (isOutbound) {
          data.civilIdExpiryDate = getUnixTimeStamp(civilIdExpiryDate.value as Date)
        }

        if (props.isEdit) {
          data.email = email.value
          data.phone = phone.value
        }

        await commonStore.sendFormData(data)
        const countries = userStore.destination?.map((country) => country?.id || '')

        if (nationality.value) {
          if (props.isEdit) {
            gaService.sendInsuredTravelersUpdateProceed(
              props.typeOfTravel,
              nationality.value.nationalityName
            )
          } else {
            gaService.sendPersonalInformationProceed(
              props.typeOfTravel,
              nationality.value.nationalityName,
              countries,
              dateOfBirth.value as Date,
              gender.value
            )
            pushEvent('form_submit', {
              product: 'travel',
              category: props.typeOfTravel,
              page: 'holder-info',
              first_name: firstName.value,
              family_name: familyName.value,
              date_of_birth: formatAnalyticsDate(dateOfBirth.value as Date),
              qid: isOutbound ? emiratesId.value : '',
              qid_expiry_date: isOutbound
                ? formatAnalyticsDate(civilIdExpiryDate.value as Date)
                : '',
              nationality: nationality.value.nationalityName,
              passport_number: passportNumber.value,
              email: email.value,
              phone_number: phone.value,
              gender: gender.value,
            })
          }
        }

        emit(PROCEED_EVENT_NAME)
      })
    }
  }

  const onFieldCompleted = (name: string, value: string | Date | null, position: number) => {
    if (!value || props.isEdit || !isOutbound) {
      return
    }

    gaService.sendPersonalInformationFieldCompleted(name, position)
  }

  watch(
    () => dateOfBirth.value,
    (current, old) => {
      if (!old && current) {
        onFieldCompleted('dob', dateOfBirth.value, 4)
      }
    }
  )

  watch(
    () => nationalityCode.value,
    () => {
      if (nationalityCode.value) {
        onFieldCompleted('nationality', nationalityCode.value, 5)
      }

      const newValue = nationalities.value.find(
        (option) => option.nationalityCode === nationalityCode.value
      )
      if (newValue) nationality.value = newValue
    }
  )

  const onEmiratesIdCompleted = (value: string) => {
    let newBirthdate: Date | undefined
    let newNat: Nationality | undefined
    let newNatCode: string | undefined

    if (!dateOfBirth.value) {
      let year = ''
      if (value[0] === '2') {
        year = '19'
      } else {
        year = '20'
      }

      year = year + value[1] + value[2]
      newBirthdate = new Date(+year, 0, 1)
    }

    if (!nationalityCode.value) {
      const iso = value[3] + value[4] + value[5]
      newNatCode = nationalities.value.find((el) => el.isoCode === iso)?.nationalityCode
      newNat = nationalities.value.find((option) => option.nationalityCode === newNatCode)
    }

    if (
      newBirthdate &&
      (newBirthdate.getTime() > maxBirthdate.getTime() ||
        newBirthdate.getTime() < minBirthdate.getTime())
    ) {
      newBirthdate = DEFAULT_START_DATE
    }

    if (newBirthdate && newNat && newNatCode) {
      gaService.sendQidFieldCompleted(newBirthdate, newNat.nationalityName)

      dateOfBirth.value = newBirthdate
      nationalityCode.value = newNatCode
    }
  }

  watch(
    () => props.loadingFormData,
    (newVal) => {
      if (!newVal) {
        firstName.value = userStore.firstName ?? ''
        familyName.value = userStore.familyName ?? ''
        dateOfBirth.value = userStore.dateOfBirth
        nationalityCode.value = userStore.nationality?.nationalityCode ?? ''
        if (isOutbound) {
          emiratesId.value = userStore.emiratesId ?? ''
          civilIdExpiryDate.value = userStore.civilIdExpiryDate
        }
        passportNumber.value = userStore.passportNumber ?? ''
        gender.value = userStore.gender ?? GENDER_MALE
        phone.value = userStore.phone ?? ''
        maskedPhone.value = userStore.maskedPhone ?? ''
        email.value = userStore.email ?? ''
      }
    }
  )
</script>

<template>
  <FlowCard
    :title="props.isEdit ? t('travellers_update_info') : $t(translatesHash.header)"
    class="personal-information"
    :with-button="false"
  >
    <div v-if="!loadingFormData" class="personal-information-blocks">
      <div class="personal-information-inputs">
        <BaseInput
          v-model="firstName"
          mask="*"
          :mask-tokens="{ '*': { pattern: /[a-zA-Z\s'-]/, multiple: true } }"
          :label="$t(translatesHash.firstName)"
          :errors="errors.firstName"
          :max-length="60"
          @blur="onFieldCompleted('first_name', firstName, 1)"
        />
        <BaseInput
          v-model="familyName"
          :label="$t(translatesHash.familyName)"
          :errors="errors.familyName"
          mask="*"
          :mask-tokens="{ '*': { pattern: /[a-zA-Z\s'-]/, multiple: true } }"
          :max-length="60"
          @blur="onFieldCompleted('family_name', familyName, 2)"
        />
        <BirthDateInput
          v-model="dateOfBirth"
          :label="$t(translatesHash.birthdate)"
          :max-age="maxAge"
          :errors="errors.dateOfBirth"
          @init="(e) => initBirthdate(e.maxDate, e.minDate)"
        />
        <div class="personal-information-inputs__gender">
          <BaseRadio v-model="gender" value="1" :label="$t('outbound.travelers.gender.male')" />
          <BaseRadio v-model="gender" value="2" :label="$t('outbound.travelers.gender.female')" />
        </div>
        <EmiratesIdInput
          v-if="isOutbound"
          v-model="emiratesId"
          :errors="errors.emiratesId"
          class="personal-information-inputs__emirates-id"
          @completed="onEmiratesIdCompleted"
        />
        <QIDExpiryDateInput
          v-if="isOutbound"
          v-model="civilIdExpiryDate"
          :errors="errors.civilIdExpiryDate"
        />
        <NationalityInput
          v-model="nationalityCode"
          :options="nationalities"
          :label="$t(translatesHash.nationality)"
          :errors="errors.nationalityCode"
        />
        <PasportNumberInput
          v-model="passportNumber"
          :errors="errors.passportNumber"
          @blur="onFieldCompleted('passport_number', passportNumber, 6)"
        />
      </div>

      <BaseFoldingBlock v-if="props.withWinterSport" class="personal-information__winter" is-open>
        <template #title>
          <div class="personal-information__winter-title">
            {{ $t('outbound_sport_cover_winter_name') }}
          </div>
        </template>

        {{ $t('outbound_sport_cover_winter_description') }}

        <template #after>
          <div class="personal-information__winter-toggle">
            <div class="personal-information__winter-price ltr">
              +{{ $t('qar') }} {{ winterSportPrice }}
            </div>
            <ToggleInput v-model="isWinterSportChecked" @click.stop />
          </div>
        </template>
      </BaseFoldingBlock>

      <div v-if="isEdit" class="personal-information-inputs">
        <h2 class="personal-information-inputs__title">
          <ContactsIcon />{{ $t(translatesHash.contacts) }}
        </h2>
        <BaseEmailInput
          v-model="email"
          v-model:errors-custom="errorsCustom"
          :label="$t(translatesHash.email)"
          :errors="errors.email"
        />
        <PhoneInput
          v-model="phone"
          v-model:masked-value="maskedPhone"
          v-model:country-code="countryCode"
          v-model:completed="phoneCompleted"
          :errors="[...errors.phone, ...errors.phoneCompleted]"
          :excluded-countries="['IL']"
        />
      </div>
    </div>

    <template #after>
      <div class="personal-information__footer">
        <FlowDataProtectReminder />
      </div>
    </template>

    <template #button>
      <BaseButton
        class="personal-information__button"
        :icon="props.isEdit ? 'none' : 'arrow-right'"
        @click="onProceed"
      >
        {{ props.isEdit ? $t('inbound_travelers_edit_save_button') : $t('quote_proceed') }}
      </BaseButton>
    </template>
  </FlowCard>
</template>

<style lang="scss">
  @import './FlowPersonalInformationWidget';
</style>
